'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';

interface LiveCallConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function LiveCallConsentModal({ open, onOpenChange, onConfirm }: LiveCallConsentModalProps) {
  const t = useTranslations('vidu-stream.compliance');
  const [hasAgreed, setHasAgreed] = useState(false);

  useEffect(() => {
    if (open) {
      setHasAgreed(false);
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="w-[min(560px,calc(100%-32px))] max-w-none sm:w-[calc(100%-24px)] sm:gap-3 sm:p-4"
        closable
        onClose={() => onOpenChange(false)}
        closeClassName="sm:top-4 sm:right-4"
        disableOverlayClose
      >
        <DialogHeader className="gap-2 sm:gap-1 sm:pr-10">
          <DialogTitle className="break-words text-left text-xl sm:text-base">{t('title')}</DialogTitle>
          <DialogDescription className="break-words text-left text-sm leading-6 sm:text-xs sm:leading-5">
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-3">
          <div className="rounded-16 bg-system-white04 p-4 text-sm text-system-white64 leading-6 sm:p-3 sm:text-xs sm:leading-5">
            {t('tip')}
          </div>

          <div className="flex items-start gap-3 rounded-12 border border-system-white08 bg-system-white04 p-4 sm:gap-2.5 sm:p-3">
            <Checkbox
              className="mt-0.5 shrink-0 text-base text-system-white sm:mt-0"
              checked={hasAgreed}
              onCheckedChange={(checked) => setHasAgreed(Boolean(checked))}
            />
            <span className="min-w-0 flex-1 text-sm text-system-white leading-6 sm:text-xs sm:leading-5">
              {t('agreePrefix')}
              <ClientLink
                className="mx-1 text-system-white underline underline-offset-4"
                href="/consent-live"
                rel="noreferrer"
                target="_blank"
              >
                {t('rulesLinkText')}
              </ClientLink>
            </span>
          </div>

          <p className="text-sm text-system-white64 leading-6 sm:text-xs sm:leading-5">
            {t('privacyPrefix')}
            <ClientLink
              className="text-system-white underline underline-offset-4"
              href="/privacy"
              rel="noreferrer"
              target="_blank"
            >
              {t('privacyLinkText')}
            </ClientLink>
          </p>

          <div className="flex flex-wrap justify-end gap-3 pt-2 sm:flex-col-reverse sm:items-stretch sm:gap-2 sm:pt-1">
            <Button variant="outline" size="dialog" className="sm:w-full" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              size="dialog"
              className="disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24 disabled:opacity-100 sm:w-full"
              disabled={!hasAgreed}
              onClick={handleConfirm}
            >
              {t('confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
